import { sendPasswordResetEmail } from "firebase/auth";
import { MailCheck } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { AuthEmailError, requestPasswordResetEmail, usesServerEmail } from "@shared/auth-email";
import { useAuth } from "../../auth/AuthProvider";
import { Button, ButtonLink } from "../../components/Button";
import { FormAlert, TextField, focusFirstInvalid } from "../../components/fields";
import { useDocumentTitle } from "../../components/layout";
import { authErrorMessage } from "../../lib/firebase";
import { AuthLayout } from "./AuthLayout";
import { EMAIL_RE } from "./Login";

export function ForgotPasswordPage() {
  useDocumentTitle("Reset password");
  const { auth, config } = useAuth();
  const [params] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [error, setError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const value = email.trim();
    if (!value || !EMAIL_RE.test(value)) {
      setError(value ? "Enter a valid email address, like sam@example.com." : "Enter the email you signed up with.");
      focusFirstInvalid(formRef.current);
      return;
    }
    setError(undefined);
    setFormError(null);
    setBusy(true);
    try {
      if (usesServerEmail(config)) {
        // The server answers the same way for every address; it never says whether an account exists.
        await requestPasswordResetEmail(value);
      } else {
        await sendPasswordResetEmail(auth, value, { url: `${window.location.origin}/login?email=${encodeURIComponent(value)}` });
      }
      setSentTo(value);
    } catch (err) {
      const code = typeof err === "object" && err && "code" in err ? String((err as { code: string }).code) : "";
      // Do not reveal whether an account exists.
      if (code === "auth/user-not-found") setSentTo(value);
      else setFormError(err instanceof AuthEmailError ? err.message : authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (sentTo) {
    return (
      <AuthLayout title="Check your email" footer={<Link to="/login">Back to sign in</Link>}>
        <div className="auth-sent">
          <MailCheck size={40} aria-hidden="true" />
          <p>
            If an account exists for <strong>{sentTo}</strong>, we've sent a link to choose a new password. The link expires after a while — request another if it does.
          </p>
          {config.devOutbox ? (
            <ButtonLink to="/dev/outbox" emphasis="outline" intent="info" block>
              Open the local outbox
            </ButtonLink>
          ) : null}
          <Button emphasis="ghost" intent="neutral" onClick={() => setSentTo(null)}>
            Use a different email
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" lead="Enter your email and we'll send you a link to choose a new password." footer={<Link to="/login">Back to sign in</Link>}>
      <form ref={formRef} noValidate onSubmit={submit} className="stack">
        <FormAlert>{formError}</FormAlert>
        <TextField label="Email" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} error={error} onChange={(e) => setEmail(e.target.value)} required />
        <Button type="submit" emphasis="solid" intent="brand" size="lg" block busy={busy}>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
